import React, { useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

interface RequireAuthProps {
  children: React.ReactNode;
  redirectTo?: string;
  fallback?: React.ReactNode;
}

/**
 * RequireAuth component that protects routes by requiring authentication.
 *
 * Usage:
 * - Wrap protected routes/components with this component
 * - If user is not authenticated, shows auth modal instead of content
 * - After authentication, renders the protected content
 *
 * @param children - The protected content to render when authenticated
 * @param redirectTo - Optional URL to redirect to after authentication
 * @param fallback - Optional loading component to show while checking auth
 */
export function RequireAuth({ children, redirectTo, fallback }: RequireAuthProps) {
  const { isSignedIn, isLoaded } = useUser();
  const { openAuthModal, isAuthModalOpen } = useAuth();
  const navigate = useNavigate();
  const hasShownModal = useRef(false);

  useEffect(() => {
    if (!isLoaded || isSignedIn) return;

    if (!hasShownModal.current) {
      // First time on this protected route — show the modal
      hasShownModal.current = true;
      openAuthModal('sign-in', redirectTo);
    } else if (!isAuthModalOpen) {
      // Modal was dismissed without signing in — send them back to hub
      navigate('/games');
    }
  }, [isLoaded, isSignedIn, isAuthModalOpen, openAuthModal, navigate, redirectTo]);

  // Show loading state while Clerk is initializing
  if (!isLoaded) {
    return fallback || (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  // Show protected content only when authenticated
  if (isSignedIn) {
    return <>{children}</>;
  }

  // Show fallback or loading state while auth modal is open
  return fallback || (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="text-center">
        <p className="text-muted-foreground mb-4">Authentication required</p>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto"></div>
      </div>
    </div>
  );
}

export default RequireAuth;